import api from './api';
import { downloadBinaryFile } from './fileDownload';

const TICKET_CACHE_PREFIX = 'tickets:';
const REPORT_CACHE_PREFIX = 'reports:';

const invalidateTicketReadCaches = (ticketId = null) => {
  api.invalidateCache(
    TICKET_CACHE_PREFIX,
    REPORT_CACHE_PREFIX,
    'branches:',
    'users:ticket-assignable'
  );

  if (ticketId !== null && ticketId !== undefined) {
    api.invalidateCache(`${TICKET_CACHE_PREFIX}detail:${ticketId}`);
  }
};

const uploadTicketFile = async (url, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(url, formData);
  return response.data;
};

const ticketService = {
  getAll: async (filters = {}, pagination = {}) => {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) params.append(key, filters[key]);
    });
    // Pagination parameters
    if (pagination.page) params.append('page', pagination.page);
    if (pagination.limit) params.append('limit', pagination.limit);
    const query = params.toString();
    const response = await api.cachedGet(`/tickets?${query}`, {}, {
      cacheKey: `${TICKET_CACHE_PREFIX}list:${query}`,
      ttlMs: 5 * 1000
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.cachedGet(`/tickets/${id}`, {}, {
      cacheKey: `${TICKET_CACHE_PREFIX}detail:${id}`,
      ttlMs: 0
    });
    return response.data;
  },

  create: async (ticketData) => {
    const response = await api.post('/tickets', ticketData);
    invalidateTicketReadCaches(response.data?.ticket?.id ?? null);
    return response.data;
  },

  update: async (id, ticketData) => {
    const response = await api.put(`/tickets/${id}`, ticketData);
    invalidateTicketReadCaches(id);
    return response.data;
  },

  addComment: async (ticketId, commentData) => {
    const response = await api.post(`/tickets/${ticketId}/comments`, commentData);
    invalidateTicketReadCaches(ticketId);
    return response.data;
  },

  updateComment: async (ticketId, commentId, commentData) => {
    const response = await api.put(`/tickets/${ticketId}/comments/${commentId}`, commentData);
    invalidateTicketReadCaches(ticketId);
    return response.data;
  },

  uploadAttachment: async (ticketId, file) => {
    const data = await uploadTicketFile(`/tickets/${ticketId}/attachments`, file);
    invalidateTicketReadCaches(ticketId);
    return data;
  },

  uploadSolutionAttachment: async (ticketId, file) => {
    const data = await uploadTicketFile(`/tickets/${ticketId}/solution-attachment`, file);
    invalidateTicketReadCaches(ticketId);
    return data;
  },

  sendResolutionEmail: async (ticketId, payload = {}) => {
    const response = await api.post(`/incoming-emails/tickets/${ticketId}/send-resolution-email`, payload);
    return response.data;
  },

  downloadSolutionAttachment: async (ticketId) => {
    return downloadBinaryFile(
      api,
      `/tickets/${ticketId}/solution-attachment`,
      `ticket-${ticketId}-solution-attachment`
    );
  },

  downloadAttachment: async (ticketId, attachmentId, fileName = null) => {
    return downloadBinaryFile(
      api,
      `/tickets/${ticketId}/attachments/${attachmentId}`,
      fileName || `ticket-${ticketId}-attachment-${attachmentId}`
    );
  },

  delete: async (id) => {
    const response = await api.delete(`/tickets/${id}`);
    invalidateTicketReadCaches(id);
    return response.data;
  },

  getTicketAssignees: async (ticketId) => {
    const response = await api.cachedGet(`/tickets/${ticketId}/assignees`, {}, {
      cacheKey: `${TICKET_CACHE_PREFIX}assignees:${ticketId}`,
      ttlMs: 5 * 1000
    });
    return response.data;
  },

  assignUsers: async (ticketId, userIds, replaceExisting = false) => {
    const normalizedUserIds = Array.isArray(userIds)
      ? [...new Set(
          userIds
            .map((id) => Number.parseInt(id, 10))
            .filter((id) => Number.isInteger(id))
        )]
      : [];

    const response = await api.post(`/tickets/${ticketId}/assignees`, {
      assignee_ids: normalizedUserIds,
      replace_existing: replaceExisting
    });
    invalidateTicketReadCaches(ticketId);
    return response.data;
  },

  // Self-assign: technician can only self-assign to unassigned tickets
  selfAssign: async (ticketId) => {
    const response = await api.post(`/tickets/${ticketId}/self-assign`);
    invalidateTicketReadCaches(ticketId);
    return response.data;
  },

  removeAssignee: async (ticketId, userId) => {
    const response = await api.delete(`/tickets/${ticketId}/assignees/${userId}`);
    invalidateTicketReadCaches(ticketId);
    return response.data;
  },

  getAssignableUsers: async () => {
    const response = await api.cachedGet('/users/ticket-assignable', {}, {
      cacheKey: 'users:ticket-assignable',
      ttlMs: 5 * 60 * 1000
    });
    return response.data.users || response.data;
  },

  // Legacy aliases kept for older callers during the refactor.
  getTicketTechnicians: async (ticketId) => ticketService.getTicketAssignees(ticketId),
  assignTechnicians: async (ticketId, technicianIds, replaceExisting = false) => (
    ticketService.assignUsers(ticketId, technicianIds, replaceExisting)
  ),
  removeTechnician: async (ticketId, technicianId) => (
    ticketService.removeAssignee(ticketId, technicianId)
  ),
  getAllTechnicians: async () => ticketService.getAssignableUsers(),
  getAllSupervisors: async () => ticketService.getAssignableUsers(),
};

export default ticketService;
